import * as React from 'react';
import { CallbackWidget } from '@cian/callback-widget';
import { useSelector } from 'react-redux';

import { selectAnalyticsUser, selectGaLabel } from '../../selectors/analytics';
import { selectBuilderId, selectCallbackPhone, selectTimezone } from '../../selectors/newbuilding';
import { selectUserPhone } from '../../selectors/user';
import { requestCallback } from '../../services/callbackRequest';
import { getWorkTimeSchedule } from '../../services/workTime';
import { IRequestCallbackParams } from '../../types/callback';
import { useApplicationContext } from '../../utils/applicationContext';
import { trackCallbackPopupSend } from '../../utils/analytics';
import { getPrivacyPolicyUrl, getSmsDisclaimerState } from './helpers';
import { ICallbackParams, TRequestCallbackResult } from '@cian/callback-widget/es/types/api';

interface ICallbackPopupContainerProps {
  onClose(): void;
  open: boolean;
}

type TRequestCallbackFn = (params: ICallbackParams) => TRequestCallbackResult;

export function CallbackPopupContainer({ onClose, open }: ICallbackPopupContainerProps) {
  const { config, httpApi, logger } = useApplicationContext();

  const agentPhone = useSelector(selectCallbackPhone);
  const builderId = useSelector(selectBuilderId);
  const gaLabel = useSelector(selectGaLabel);
  const user = useSelector(selectAnalyticsUser);
  const userPhone = useSelector(selectUserPhone);
  const timezone = useSelector(selectTimezone);

  const privacyPolicyUrl = getPrivacyPolicyUrl(config);
  const isSMSDisclaimerEnabled = getSmsDisclaimerState(config);

  const onRequestCallback = React.useCallback(
    async (parameters: IRequestCallbackParams) => {
      const result = await requestCallback({ httpApi, logger }, parameters);

      const extra: Record<string, string | boolean> = {
        call_now: parameters.callRightNow,
        call_time: parameters.time,
        phone: parameters.customerPhone,
      };

      trackCallbackPopupSend({
        label: gaLabel,
        user: { ...user, extra },
      });

      return result;
    },
    [gaLabel, user, httpApi, logger],
  );

  const handleRequestWorkTime = React.useCallback(async () => {
    const result = await getWorkTimeSchedule({ httpApi, logger }, { userId: builderId as number });

    return result;
  }, [builderId, httpApi, logger]);

  if (!agentPhone) {
    return null;
  }

  return (
    <CallbackWidget
      agentPhone={agentPhone}
      onClose={onClose}
      onRequestCallback={onRequestCallback as TRequestCallbackFn}
      onRequestWorkTime={handleRequestWorkTime}
      open={open}
      privacyPolicyUrl={privacyPolicyUrl}
      timezoneOffset={timezone}
      userPhoneNumber={userPhone}
      isSMSDisclaimerEnabled={isSMSDisclaimerEnabled}
    />
  );
}
